<template>
  <ais-instant-search
    :search-client="searchClient"
    :index-name="mainIndex"
    :future="{ preserveSharedStateOnUnmount: true }"
    :initial-ui-state="initialUiState"
    :stalled-search-delay="200"
    :routing="{
      stateMapping: singleIndex(mainIndex),
    }"
  >
    <ais-configure :hits-per-page.camel="1000" />

    <ais-search-box
      :show-loading-indicator="false"
      placeholder="Search query"
      :class-names="{
        'ais-SearchBox': 'search-box',
        'ais-SearchBox-form': 'search-box__form',
        'ais-SearchBox-input': 'search-box__input',
        'ais-SearchBox-submit': 'search-box__submit',
        'ais-SearchBox-submitIcon': 'search-box__submit-icon',
        'ais-SearchBox-reset': 'search-box__reset',
        'ais-SearchBox-resetIcon': 'search-box__reset-icon',
        'ais-SearchBox-loadingIndicator': 'search-box__loading',
        'ais-SearchBox-loadingIcon': 'search-box__loading-icon',
      }"
    />

    <ais-state-results class="search-results">
      <template #default="{ state: { query } }">
        <div v-if="totalResultsCount && query.length > 0" class="search-results__total-results">
          {{ totalResultsCount }} result{{ totalResultsCount > 1 ? 's' : '' }} found
        </div>
        <div class="search-results__inner has-border-links" v-show="query.length > 0">
          <div v-show="globalResultsCount.pages" class="search-results-section">
            <h2 class="search-results-section__title is-h2">Pages</h2>
            <ais-hits
              :class-names="{
                'ais-Hits': 'search-results-section__hits',
                'ais-Hits-list': 'search-results-section__hits-list',
                'ais-Hits-item': 'search-results-section__hits-item',
              }"
            >
              <template #item="{ item }">
                <page-card :item="item" />
              </template>
            </ais-hits>
          </div>
          <ais-index :index-name="searchConfig.indexesNames.properties">
            <div v-show="globalResultsCount.properties" class="search-results-section">
              <h2 class="search-results-section__title is-h2">Properties</h2>
              <ais-hits
                :class-names="{
                  'ais-Hits': 'search-results-section__hits',
                  'ais-Hits-list': 'search-results-section__hits-list',
                  'ais-Hits-item': 'search-results-section__hits-item',
                }"
              >
                <template #item="{ item }">
                  <property-card :item="item" />
                </template>
              </ais-hits>
            </div>
          </ais-index>
          <ais-index :index-name="searchConfig.indexesNames.news">
            <div v-show="globalResultsCount.news" class="search-results-section">
              <h2 class="search-results-section__title is-h2">News</h2>
              <ais-hits
                :class-names="{
                  'ais-Hits': 'search-results-section__hits',
                  'ais-Hits-list': 'search-results-section__hits-list',
                  'ais-Hits-item': 'search-results-section__hits-item',
                }"
              >
                <template #item="{ item }">
                  <news-card :item="item" />
                </template>
              </ais-hits>
            </div>
          </ais-index>
        </div>
        <template v-if="!totalResultsCount && query.length > 0">
          <div class="search-results__empty">
            <p>Nothing found for your query</p>
          </div>
        </template>
      </template>
    </ais-state-results>
  </ais-instant-search>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import algoliasearch from 'algoliasearch/lite';
import { AisHits, AisIndex, AisInstantSearch, AisConfigure, AisStateResults, AisSearchBox } from 'vue-instantsearch/vue3/es';
import { singleIndex } from 'instantsearch.js/es/lib/stateMappings';
import PageCard from './partials/PageCard.vue';
import NewsCard from './partials/NewsCard.vue';
import PropertyCard from './partials/PropertyCard.vue';

const searchConfig = inject('searchConfig');
const mainIndex = searchConfig.indexesNames.pages;

const algoliaClient = algoliasearch(searchConfig.appId, searchConfig.apiKey);

const globalResultsCount = ref({});
const totalResultsCount = computed(() => Object.values(globalResultsCount.value).reduce((a, b) => a + b, 0));

const searchClient = {
  ...algoliaClient,
  async search(requests) {
    if (requests.every(({ params }) => !params?.query)) {
      const responses = {
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: '',
        })),
      };
      updateResultsCount(responses.results);
      return Promise.resolve(responses);
    }

    const responses = await algoliaClient.search(requests);
    updateResultsCount(responses.results);
    return responses;
  },
};

const updateResultsCount = (results) => {
  if (!results || !Array.isArray(results)) {
    return;
  }
  globalResultsCount.value = results.reduce((curr, result) => {
    if (result) {
      const indexName = getIndexByValue(searchConfig.indexesNames, result.index);
      if (indexName) {
        curr[indexName] = result.nbHits || 0;
      }
    }
    return curr;
  }, {});
};

const urlSearchParams = new URLSearchParams(window.location.search);

const initialUiState = {
  [mainIndex]: {
    query: urlSearchParams.get('query') || '',
  },
};

const getIndexByValue = (object, index) => Object.keys(object).reduce((curr, indexName) => (object[indexName] === index ? indexName : curr), '');
</script>
